import type { POI } from "@/types/fleet";
import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";

type RemotePoiType = "ostalo" | "partner" | "pumpa" | "magacin" | "kupac" | "servis";

interface RemotePoi {
  id: string;
  title: string;
  description?: string;
  x: string;
  y: string;
  /** Not returned by GET until ClientPOI's FormFields entry gains `'api' => true`. */
  type?: RemotePoiType;
  address?: string;
  kontakt_osoba?: string;
  kontakt_telefon?: string;
  kontakt_email?: string;
  note?: string;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

/** Upstream uses its own enum vocabulary, so the type is translated in both directions. */
const REMOTE_TYPE_BY_LOCAL: Record<POI["type"], RemotePoiType> = {
  partner: "partner",
  gas_station: "pumpa",
  warehouse: "magacin",
  customer: "kupac",
  service_center: "servis",
  other: "ostalo",
};

const LOCAL_TYPE_BY_REMOTE: Record<RemotePoiType, POI["type"]> = {
  partner: "partner",
  pumpa: "gas_station",
  magacin: "warehouse",
  kupac: "customer",
  servis: "service_center",
  ostalo: "other",
};

/**
 * Upstream neither validates the enum nor (yet) returns it, so a missing or unrecognised
 * value falls back to `other` rather than breaking the icon/stat lookups keyed on it.
 */
function fromRemoteType(type?: string): POI["type"] {
  if (!type) return "other";
  return LOCAL_TYPE_BY_REMOTE[type as RemotePoiType] ?? "other";
}

function toRemoteType(type: POI["type"]): RemotePoiType {
  return REMOTE_TYPE_BY_LOCAL[type] ?? "ostalo";
}

const COORD_DECIMALS = 6;

/**
 * Upstream validates `x`/`y` with PHP `empty()`, so a coordinate of exactly 0 sent as "0"
 * comes back as 400 missing_fields. Fixed decimals keep the equator/Greenwich case writable.
 */
function formatCoord(value: number) {
  return value.toFixed(COORD_DECIMALS);
}

function parseNumber(value?: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Before ClientPOI had real `address`/`note` columns, writes packed both into `description`
 * joined with " | ". Split that back only when the separator is present and the dedicated
 * columns are empty; saving the record again writes the real columns and retires this path.
 */
function readLegacyDescription(remote: RemotePoi) {
  if (remote.address || remote.note) return null;
  const value = remote.description ?? "";
  const separator = value.indexOf(" | ");
  if (separator === -1) return null;
  return {
    address: value.slice(0, separator),
    notes: value.slice(separator + 3),
  };
}

function mapPoi(remote: RemotePoi): POI {
  const legacy = readLegacyDescription(remote);
  return {
    id: remote.id,
    name: remote.title,
    type: fromRemoteType(remote.type),
    latitude: parseNumber(remote.y),
    longitude: parseNumber(remote.x),
    address: legacy?.address ?? remote.address ?? "",
    notes: legacy?.notes ?? remote.note ?? "",
    description: legacy ? "" : remote.description ?? "",
    contactPerson: remote.kontakt_osoba ?? "",
    contactPhone: remote.kontakt_telefon ?? "",
    contactEmail: remote.kontakt_email ?? "",
  };
}

export interface PoiInput {
  name: string;
  type: POI["type"];
  latitude: number;
  longitude: number;
  address?: string;
  description?: string;
  notes?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export async function listPois(): Promise<POI[]> {
  const response = await apiRequest<ListResponse<RemotePoi>>("/api/pois", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map(mapPoi);
}

export async function createPoi(input: PoiInput): Promise<void> {
  const response = await apiRequest<{ success: true; id: string }>("/api/pois", {
    method: "POST",
    body: JSON.stringify({
      title: input.name,
      type: toRemoteType(input.type),
      x: formatCoord(input.longitude),
      y: formatCoord(input.latitude),
      address: input.address ?? "",
      description: input.description ?? "",
      note: input.notes ?? "",
      kontakt_osoba: input.contactPerson ?? "",
      kontakt_telefon: input.contactPhone ?? "",
      kontakt_email: input.contactEmail ?? "",
    }),
  });
  logCrud("create", "poi", input.name, response.id);
}

export async function updatePoi(id: string, input: PoiInput): Promise<void> {
  // Upstream PUT treats every field as optional, so only send what the caller supplied.
  const body: Record<string, string> = {
    title: input.name,
    type: toRemoteType(input.type),
    x: formatCoord(input.longitude),
    y: formatCoord(input.latitude),
  };
  if (input.address !== undefined) {
    body.address = input.address;
  }
  if (input.description !== undefined) {
    body.description = input.description;
  }
  if (input.notes !== undefined) {
    body.note = input.notes;
  }
  if (input.contactPerson !== undefined) {
    body.kontakt_osoba = input.contactPerson;
  }
  if (input.contactPhone !== undefined) {
    body.kontakt_telefon = input.contactPhone;
  }
  if (input.contactEmail !== undefined) {
    body.kontakt_email = input.contactEmail;
  }

  await apiRequest<{ success: true }>(`/api/pois/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  logCrud("update", "poi", input.name, id);
}

export async function deletePoi(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/pois/${id}`, {
    method: "DELETE",
  });
  logCrud("delete", "poi", "", id);
}
